import { allScreenComponents, selectedScreen } from './nativeScreens.js'
import homeNavigation from '../../../../contracts/native-sportsbook/v1/home-navigation.json' with { type: 'json' }
import registryBundle from '../../../../contracts/native-sportsbook/v1/component-registry.json' with { type: 'json' }
import { columnsFromTemplate, templatesFor } from './playerPropBoardModel.js'
import { leagueFor } from './parlayBuilderModel.js'

const WORD_INITIAL_PATTERN = /\b\w/g

export const COMPONENT_REGISTRY = registryBundle
export const CAPABILITIES = registryBundle.components.map((component) => component.capability).sort()
export const COMPONENT_DEFINITIONS = Object.fromEntries(registryBundle.components.map((component) => [component.type, component]))

export const CATALOG_SCOPES = [
  { value: 'BASEBALL,AMERICAN_FOOTBALL,FOOTBALL,BASKETBALL,ICE_HOCKEY,TENNIS', label: 'All demo sports' },
  { value: 'BASEBALL', label: 'Baseball' },
  { value: 'MLB', label: 'MLB' },
  { value: 'AMERICAN_FOOTBALL', label: 'American football' },
  { value: 'NFL', label: 'NFL' },
  { value: 'FOOTBALL', label: 'Soccer' },
  { value: 'BASKETBALL', label: 'Basketball' },
  { value: 'NBA', label: 'NBA' },
  { value: 'ICE_HOCKEY', label: 'Ice hockey' },
  { value: 'NHL', label: 'NHL' },
  { value: 'TENNIS', label: 'Tennis' },
]

export function catalogIdentifierLabel(identifier) {
  const configured = CATALOG_SCOPES.find((scope) => scope.value === identifier)
  if (configured) return configured.label
  return identifier
    .toLowerCase()
    .replaceAll('_', ' ')
    .replace(WORD_INITIAL_PATTERN, (character) => character.toUpperCase())
}

const BETPARX_DARK_COLORS = {
  background: '#190532',
  background_elevated: '#2F1747',
  surface: '#351B4D',
  surface_subtle: '#3D2454',
  surface_interactive: '#452E58',
  text_primary: '#FFFFFF',
  text_secondary: '#CCC8D2',
  text_tertiary: '#A9ABB7',
  accent: '#E36DF9',
  brand_secondary: '#E61E37',
  border_subtle: '#61457D',
  positive: '#30C785',
  negative: '#FF6375',
  warning: '#FFC30A',
  highlight: '#FFD166',
}

const BETPARX_LIGHT_COLORS = {
  background: '#F2F1F3',
  background_elevated: '#FFFFFF',
  surface: '#FFFFFF',
  surface_subtle: '#F2F2F2',
  surface_interactive: '#F7F1FE',
  text_primary: '#16121C',
  text_secondary: '#53576F',
  text_tertiary: '#6B6870',
  accent: '#A700C4',
  brand_secondary: '#E61E37',
  border_subtle: '#CCC8D2',
  positive: '#147A50',
  negative: '#C81319',
  warning: '#815B00',
  highlight: '#8A6100',
}

function betparxTheme(defaultAppearance) {
  return {
    name: 'betPARX',
    colors: BETPARX_DARK_COLORS,
    appearance: {
      default: defaultAppearance,
      schemes: { dark: {}, light: BETPARX_LIGHT_COLORS },
    },
    typography: {
      display_family: 'brand_display',
      body_family: 'brand_body',
      number_family: 'system_monospaced',
    },
  }
}

export const THEMES = {
  betparxLight: {
    label: 'betPARX Light',
    ...betparxTheme('light'),
  },
  betparxDark: {
    label: 'betPARX Dark',
    ...betparxTheme('dark'),
  },
}

const THEME_SCALES = {
  spacing: { xs: 4, sm: 8, md: 16, lg: 24, xl: 32 },
  radius: { sm: 4, md: 8, lg: 16 },
  motion: { fast_ms: 120, standard_ms: 220 },
}

export function defaultThemeDocument(presetKey = 'betparxLight') {
  const preset = structuredClone(THEMES[presetKey])
  delete preset.label
  return { ...preset, ...structuredClone(THEME_SCALES) }
}

function eventList(id, slotId, title, sportIds) {
  return { id, slot_id: slotId, enabled: true, type: 'event_list', visibility: { jurisdictions: ['US-NJ'], authenticated: 'any' }, title, sport_ids: sportIds, card_density: 'comfortable', metadata_style: 'full', market_priority: 'sport_default', market_count: 3 }
}

// Seeds follow the first live NBA game rather than naming a sample id, so a
// tenant's first draft resolves against whatever inventory it previews.
function liveGameMomentum(id, slotId) {
  return {
    id,
    slot_id: slotId,
    enabled: true,
    type: 'live_game_momentum',
    visibility: { jurisdictions: ['US-NJ'], authenticated: 'any' },
    title: 'Live game momentum',
    event_selector: { mode: 'first_live', sport_ids: ['NBA'] },
    unavailable_behavior: 'first_live',
  }
}

export const DEFAULT_MANIFEST = {
  schema_version: '1.0.0',
  manifest_id: '10000000-0000-4000-8000-000000000001',
  tenant_id: '20000000-0000-4000-8000-000000000001',
  environment: 'fixture',
  channel: 'preview',
  version: 1,
  issued_at: '2026-08-27T12:00:00Z',
  minimum_capabilities: CAPABILITIES,
  theme: defaultThemeDocument('betparxLight'),
  screens: [{
    id: '30000000-0000-4000-8000-000000000001',
    route: '/home',
    title: 'Sportsbook',
    navigation: structuredClone(homeNavigation),
    components: [
      liveGameMomentum('40000000-0000-4000-8000-000000000005', '50000000-0000-4000-8000-000000000005'),
      eventList('40000000-0000-4000-8000-000000000001', '50000000-0000-4000-8000-000000000001', 'CTN Events', ['BASEBALL', 'AMERICAN_FOOTBALL', 'FOOTBALL', 'BASKETBALL', 'ICE_HOCKEY', 'TENNIS']),
      eventList('40000000-0000-4000-8000-000000000002', '50000000-0000-4000-8000-000000000002', 'Major Leagues', ['MLB', 'NFL', 'NBA', 'NHL']),
      eventList('40000000-0000-4000-8000-000000000004', '50000000-0000-4000-8000-000000000004', 'Soccer', ['FOOTBALL']),
      {
        id: '40000000-0000-4000-8000-000000000003',
        slot_id: '50000000-0000-4000-8000-000000000003',
        enabled: true,
        type: 'compliance_notice',
        visibility: { jurisdictions: ['US-NJ'], authenticated: 'any' },
        notice_kind: 'responsible_gaming',
        text: 'Gambling problem? Call 1-800-GAMBLER.',
      },
    ],
  }],
}

export function editableManifest(document) {
  const copy = structuredClone(document)
  for (const screen of copy.screens) screen.components = screen.components.map((component) => ({ ...component, enabled: component.enabled !== false }))
  return reconcileManifestCapabilities(copy)
}

export function publishableManifest(draft) {
  const document = structuredClone(draft)
  delete document.theme.label
  return document
}

export function themeKeyFor(manifest) {
  return manifest?.theme?.appearance?.default === 'dark' ? 'betparxDark' : 'betparxLight'
}

export function resolvedThemeColors(theme) {
  const defaultAppearance = theme?.appearance?.default
  const overrides = theme?.appearance?.schemes?.[defaultAppearance] || {}
  return { ...(theme?.colors || {}), ...overrides }
}

export function componentName(component) {
  return COMPONENT_DEFINITIONS[component.type]?.display_name || component.type.replaceAll('_', ' ')
}

export function componentInstanceName(component) {
  return component.title || component.headline || componentName(component)
}

export function componentDefinition(type) {
  return COMPONENT_DEFINITIONS[type]
}

export function availableComponentDefinitions(manifest, registry = COMPONENT_REGISTRY, screenId) {
  const screen = selectedScreen(manifest, { screenId })
  const counts = (screen?.components || []).reduce((result, component) => {
    result[component.type] = (result[component.type] || 0) + 1
    return result
  }, {})
  return registry.components.filter((definition) =>
    definition.surfaces.includes('home') &&
    definition.policy.removable &&
    (definition.type !== 'competition_markets' || screen?.context?.kind === 'competition') &&
    (counts[definition.type] || 0) < definition.policy.max_instances)
}

// The app's registered deep-link destinations (DeepLinkRouter.swift). Manifest
// buttons can only point here or at one event.
export const ROUTE_OPTIONS = [
  { value: '/home', label: 'Home' },
  { value: '/betslip', label: 'Betslip' },
  { value: '/my-bets', label: 'My Bets' },
  { value: '/betlab', label: 'betLab' },
  { value: '/casino', label: 'Casino' },
  { value: '/account', label: 'Account' },
]
export const EVENT_ROUTE_PREFIX = '/event/'

export function routeLabel(route) {
  const fixed = ROUTE_OPTIONS.find((option) => option.value === route)
  if (fixed) return fixed.label
  if (route?.startsWith(EVENT_ROUTE_PREFIX)) return `Event ${route.slice(EVENT_ROUTE_PREFIX.length)}`
  return route || 'Not set'
}

export function cardKinds(type = 'featured_showcase', registry = COMPONENT_REGISTRY) {
  return registry.components.find((definition) => definition.type === type)?.editor?.cards?.kinds || []
}

export function cardKindDefinition(kind, registry = COMPONENT_REGISTRY) {
  return cardKinds('featured_showcase', registry).find((definition) => definition.kind === kind)
}

export function cardName(card, registry = COMPONENT_REGISTRY) {
  return card.headline || cardKindDefinition(card.kind, registry)?.display_name || card.kind.replaceAll('_', ' ')
}

export function createCard(kind, createUUID = () => globalThis.crypto.randomUUID()) {
  const identity = { id: createUUID(), kind }
  switch (kind) {
    case 'promotion':
      return { ...identity, headline: 'Bet $20, get $100 in bonus bets', body: 'New customers only. Opt in, place your first $20 wager, and your bonus bets land instantly.', badge: 'NEW CUSTOMER', action: { label: 'Claim offer', route: '/account' }, terms: '21+. NJ only. Terms apply. Gambling problem? Call 1-800-GAMBLER.' }
    case 'event_spotlight':
      return { ...identity, headline: 'Game of the night', event_selector: { mode: 'first_live', sport_ids: ['NBA'] }, action_label: 'Bet now' }
    case 'video':
      return { ...identity, headline: 'Watch: the drive that flipped the line', caption: 'Last night in 30 seconds.', video_url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4', poster_url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/images/ForBiggerBlazes.jpg', autoplay: true, action: { label: 'Bet the game', route: '/home' } }
    // Selection-backed cards start without a game: the Inspector searches the
    // catalog for one, and the preview says the app skips the card until then.
    case 'odds_boost':
      return { ...identity, headline: 'Boost of the day', body: 'Boosted from the market price for a limited time.', boosted_odds: 2500 }
    case 'parlay_suggestion':
      return { ...identity, headline: 'Suggested parlay', body: 'Our most-backed combination this week, in one tap.', legs: [
        { event_id: '', market_id: '', outcome_id: '' },
        { event_id: '', market_id: '', outcome_id: '' },
      ] }
    default:
      throw new Error(`Card ${kind} cannot be added in Studio`)
  }
}

export function createComponent(type, context = null, createUUID = () => globalThis.crypto.randomUUID()) {
  const ctx = context
  const identity = { id: createUUID(), slot_id: createUUID(), enabled: true, visibility: { jurisdictions: ['US-NJ'], authenticated: 'any' } }
  switch (type) {
    case 'hero':
      return { ...identity, type, headline: 'Featured on Wager', body: 'Add a helpful message for customers.', action: { label: 'View events', route: '/events/nfl' } }
    case 'sports_search':
      return { ...identity, type, title: 'Find your next play', placeholder: 'Teams, players, markets…', suggested_queries: ['MLB', 'NFL', 'NBA', 'NHL'] }
    case 'event_list':
      return { ...identity, type, title: 'Featured events', sport_ids: ['NFL'] }
    case 'live_game_momentum':
      return { ...identity, type, title: 'Live game momentum', event_selector: { mode: 'first_live', sport_ids: ['NBA'] }, unavailable_behavior: 'hide' }
    // Boost and parlay cards need a chosen selection to validate, so a new
    // showcase starts with the two kinds that render from the catalog alone.
    case 'featured_showcase':
      return { ...identity, type, title: 'Featured', layout: 'carousel', autoplay_seconds: 6, cards: [createCard('promotion', createUUID), createCard('event_spotlight', createUUID)] }
    case 'betwire_surface':
      return { ...identity, type, surface_id: '', layout: 'carousel', page_size: 3 }
    case 'competition_markets': {
      const kind = ctx?.kind === 'futures' ? 'futures' : 'player_props'
      const title = ctx?.title || (kind === 'futures' ? 'Futures' : 'Player props')
      const sport = ctx?.sport || 'baseball'
      const name = ctx?.competition || ctx?.name || 'MLB'
      const limit = typeof ctx?.limit === 'number' ? ctx.limit : 6
      return {
        ...identity,
        type,
        title,
        kind,
        competition: { sport, name },
        limit,
      }
    }
    // A board starts from the provider template for the screen's league (NFL
    // touchdowns on Home), so a new board previews against real criteria.
    case 'player_prop_board': {
      const sport = ctx?.sport || 'american_football'
      const competition = ctx?.competition || 'NFL'
      const template = templatesFor(sport, competition)[0]
      const columns = template ? columnsFromTemplate(template, createUUID) : { columns: [], rank_column_id: undefined }
      const component = { ...identity, type, title: template?.title || 'Player props', scope: { mode: 'competition', sport, competition }, columns: columns.columns, player_limit: 5, empty_behavior: 'hide' }
      if (columns.rank_column_id) component.rank_column_id = columns.rank_column_id
      return component
    }
    // A builder starts with the screen's league (NFL on Home) from the observed
    // provider template, so a new builder previews against real criteria.
    case 'same_game_parlay_builder':
      return { ...identity, type, title: 'Same Game Parlay+', subtitle: 'Build bigger. Bigger wins.', leagues: [leagueFor(ctx?.sport || 'american_football', ctx?.competition || 'NFL', createUUID)], player_limit: 4, empty_behavior: 'hide' }
    default:
      throw new Error(`Component ${type} cannot be added in Studio`)
  }
}

export function reconcileManifestCapabilities(manifest, registry = COMPONENT_REGISTRY) {
  const next = structuredClone(manifest)
  const capabilities = new Set()
  if (next.experience) capabilities.add('experience.composition.v1')
  if (next.screens?.some((screen) => screen.navigation)) capabilities.add('home.navigation.v1')
  for (const component of allScreenComponents(next)) {
    const definition = registry.components.find((candidate) => candidate.type === component.type)
    if (definition) capabilities.add(definition.capability)
  }
  next.minimum_capabilities = [...capabilities].sort()
  return next
}

export function themeWithAccent(theme, appearance, accent) {
  const next = structuredClone(theme)
  if (appearance === 'dark') next.colors.accent = accent
  else next.appearance.schemes.light = { ...next.appearance.schemes.light, accent }
  return next
}

export function updateThemeAccent(manifest, appearance, accent) {
  const next = structuredClone(manifest)
  next.theme = themeWithAccent(next.theme, appearance, accent)
  return next
}

export function themeWithHighlight(theme, appearance, highlight) {
  const next = structuredClone(theme)
  if (appearance === 'dark') next.colors.highlight = highlight
  else next.appearance.schemes.light = { ...next.appearance.schemes.light, highlight }
  return next
}

export function updateThemeHighlight(manifest, appearance, highlight) {
  const next = structuredClone(manifest)
  next.theme = themeWithHighlight(next.theme, appearance, highlight)
  return next
}

export function visiblePreviewComponents(manifest, jurisdiction, sessionState, screenId) {
  const authenticated = sessionState === 'authenticated'
  return (selectedScreen(manifest, { screenId })?.components || []).filter((component) => {
    if (component.enabled === false) return false
    const visibility = component.visibility || {}
    const jurisdictionAllowed = !visibility.jurisdictions?.length || visibility.jurisdictions.includes(jurisdiction)
    const sessionAllowed = visibility.authenticated === 'any' ||
      (visibility.authenticated === 'required' && authenticated) ||
      (visibility.authenticated === 'anonymous' && !authenticated)
    return jurisdictionAllowed && sessionAllowed
  })
}

export function manifestStatusLabel(status) {
  return {
    draft: 'Draft',
    pending_approval: 'Awaiting approval',
    approved: 'Approved',
    published: 'Published',
    superseded: 'Superseded',
  }[status] || 'Unavailable'
}

function srgbChannelToLinear(channel) {
  const normalized = channel / 255
  return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4
}

function relativeLuminance(hex) {
  const clean = (hex || '').replace('#', '')
  const r = parseInt(clean.slice(0, 2), 16)
  const g = parseInt(clean.slice(2, 4), 16)
  const b = parseInt(clean.slice(4, 6), 16)
  return 0.2126 * srgbChannelToLinear(r) + 0.7152 * srgbChannelToLinear(g) + 0.0722 * srgbChannelToLinear(b)
}

// WCAG 2.x contrast ratio: (L1 + 0.05) / (L2 + 0.05), L1 the lighter relative luminance.
export function contrastRatio(hexA, hexB) {
  const luminanceA = relativeLuminance(hexA)
  const luminanceB = relativeLuminance(hexB)
  const lighter = Math.max(luminanceA, luminanceB)
  const darker = Math.min(luminanceA, luminanceB)
  return (lighter + 0.05) / (darker + 0.05)
}
